import type { PickProblem } from "@edu/shared";
import { levelsLine } from "./one-topic-text.ts";

/**
 * Слова строк-пиков, которые зависят от МЕСТА: форма выдачи домашки или пачка
 * заданий на уроке (раздел 7.1 `docs/room-exercise-batch-design.md`).
 *
 * Отдельным модулем от разметки по правилу раздела 9 CLAUDE.md: тестов на
 * компоненты в проекте нет, а слова, которые видит преподаватель, проверить
 * надо, и проверяются они только тогда, когда живут в чистой функции. У
 * домашки тексты обязаны остаться прежними дословно, и держит это тест рядом.
 *
 * ⚠️ Выбор по месту картами, а не `switch`: пропущенное место здесь это ошибка
 * компиляции, а пропущенная ветка `switch` молча вернула бы `undefined` (тот же
 * приём, что у карт `homework/assign/subjects.ts`).
 */

/**
 * Где стоят строки-пики. ⚠️ У хука это ОБЯЗАТЕЛЬНЫЙ параметр без умолчания:
 * урок, забывший его передать, молча получил бы потолок пятьдесят и тексты
 * домашки, и увидели бы это только глазами.
 */
export type TopicPicksPlace = "homework" | "lesson";

/** Глагол заказа: домашку «задают», задания на уроке «собирают». */
const VERB: Record<TopicPicksPlace, string> = { homework: "задать", lesson: "собрать" };

/**
 * Перебор общего потолка у СТРОКИ. ⚠️ У урока `null`, и это не забытая ветка:
 * `rowProblem` отдаёт `over-total` каждой годной строке, а в узкой панели урока
 * три строки с одинаковым текстом это повтор сказанного. Виновата сумма, а не
 * строка, поэтому у урока текст стоит один раз, у счётчика (`totalProblemText`).
 */
const ROW_OVER_TOTAL: Record<TopicPicksPlace, ((total: number, hardCap: number) => string) | null> = {
  homework: (total, hardCap) => `Всего заданий ${total}, а потолок домашки ${hardCap}.`,
  lesson: null,
};

/** Перебор общего потолка у СЧЁТЧИКА. У домашки `null`: там он сказан у строки. */
const COUNTER_OVER_TOTAL: Record<TopicPicksPlace, ((hardCap: number) => string) | null> = {
  homework: null,
  lesson: (hardCap) => `За один раз можно собрать не больше ${hardCap}.`,
};

/**
 * Пустой справочник. ⚠️ У урока `null`, потому что ветка там мёртвая: блок
 * пачки рисуется только при непустом справочнике одиночного выбора темы, а
 * мёртвый текст однажды оживёт правкой условия и заговорит невычитанным.
 */
const EMPTY_CATALOG: Record<TopicPicksPlace, ((by: string) => string) | null> = {
  // Про платформу, а не про преподавателя: генераторов по этому предмету у
  // нас пока нет, и это единственное, о чём здесь говорится
  homework: (by) => `Тем ${by} пока нет: задания берутся из заготовки.`,
  lesson: null,
};

/** Текст проблемы у строки. `null` значит «у строки ничего не рисуется». */
export function rowProblemText(problem: PickProblem, place: TopicPicksPlace): string | null {
  switch (problem.kind) {
    case "no-topic":
      // ⚠️ `null` у обоих мест: пустая строка это ещё не выбранная тема, а не
      // ошибка, и текст под каждой свежей строкой читался бы как упрёк. Кнопку
      // при этом держит `ready`, а не этот текст
      return null;
    case "unknown-topic":
      return "Этой темы больше нет в списке: обновите страницу и выберите заново.";
    case "no-level":
      // «Такой СЛОЖНОСТИ», а не «такого уровня»: строкой выше в той же строке-пике
      // стоит уровень материала («Материал: уровень B1»), и это другая шкала
      return `Такой сложности по этой теме нет. Есть: ${levelsLine(problem.available)}.`;
    case "over-max":
      // ⚠️ Строк по одной теме может быть несколько, и сервер сложит их в один
      // заказ. Текст «можно не больше 12» у строки с семёркой читался бы как
      // враньё, поэтому при нескольких строках он называет сумму
      return problem.rows > 1
        ? `По этой теме заказано ${problem.asked} в ${problem.rows} строках, а вместе можно не больше ${problem.maxCount}: разных задач у неё столько. Строки по одной теме складываются.`
        : `По этой теме можно ${VERB[place]} не больше ${problem.maxCount}: разных задач у неё столько.`;
    case "over-total": {
      const text = ROW_OVER_TOTAL[place];
      return text === null ? null : text(problem.total, problem.hardCap);
    }
  }
}

/**
 * Перебор общего потолка у счётчика «всего заданий». Число берётся из
 * `hardCap` хука, а не пишется буквой: потолок у места один, и второе его
 * написание однажды разъедется с первым.
 */
export function totalProblemText(place: TopicPicksPlace, hardCap: number): string | null {
  const text = COUNTER_OVER_TOTAL[place];
  return text === null ? null : text(hardCap);
}

/**
 * Состояние справочника словами. `failed` одинаков у обоих мест, и у урока
 * ветка живая: справочник у блока пачки свой хук, и его запрос может упасть при
 * целом справочнике одиночного выбора. «Повторить» рядом рисует разметка.
 */
export function catalogText(
  kind: "failed" | "empty", place: TopicPicksPlace, by: string,
): string | null {
  if (kind === "failed") return `Темы ${by} не загрузились.`;
  const text = EMPTY_CATALOG[place];
  return text === null ? null : text(by);
}
